import React, { useMemo } from 'react';
import IntegrationLogo from 'components/shared/IntegrationLogo';
import './AgentWall.css';

/**
 * AgentWall — the login/landing hero showcase. A 3D-tilted, slowly drifting
 * "pivot"-style wall of agent / tool logo tiles. Pure CSS animation (no deps):
 * an oversized grid plane is rotated in 3D (perspective + rotateX/rotateZ) so
 * the tiles read as an isometric diagonal collage; each row drifts horizontally
 * on an infinite loop (rows alternate direction + speed for parallax), and the
 * whole thing fades into the dark background via a radial mask.
 *
 * Tiles use the real brand marks Zibby orchestrates (IntegrationLogo). Purely
 * decorative → aria-hidden.
 */

// The brand marks IntegrationLogo can render (anthropic = Claude, openai = Codex).
const LOGOS = [
  'anthropic', 'cursor', 'openai', 'github', 'gitlab', 'sentry', 'slack',
  'jira', 'notion', 'linear', 'figma', 'lark', 'plane',
];

const ROWS = 6;

// Rotate the base list per row so identical logos never line up into columns.
function rowLogos(rowIndex) {
  const k = (rowIndex * 5) % LOGOS.length; // *5 (coprime with 13) spreads nicely
  return LOGOS.slice(k).concat(LOGOS.slice(0, k));
}

export default function AgentWall() {
  const rows = useMemo(() => Array.from({ length: ROWS }, (_, r) => rowLogos(r)), []);

  return (
    <div className="agent-wall" aria-hidden="true">
      <div className="agent-wall__glow" />
      <div className="agent-wall__plane">
        {rows.map((logos, r) => (
          <div
            key={r}
            className={`agent-wall__row${r % 2 ? ' agent-wall__row--rev' : ''}`}
            style={{ '--dur': `${42 + r * 6}s` }}
          >
            {/* duplicated once → translateX(-50%) loops seamlessly */}
            {[...logos, ...logos].map((id, i) => (
              <div className="agent-tile" key={`${r}-${id}-${i}`}>
                <span className="agent-tile__logo">
                  <IntegrationLogo id={id} size={44} />
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
